import { Box, Paper, Divider, Typography, Stack } from "@mui/material";
import { FormField } from "../../../components/FormField/FormField";
import { ProfilePictureSection } from "./ProfilePictureSection";
import { ReactNode } from "react";

export interface PersonalInfoFieldConfig {
  key: string;
  label: string | ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  render?: (config: PersonalInfoFieldConfig) => ReactNode; // For custom rendering (e.g., Reset Password link)
  error?: string;
}

interface PersonalInfoSectionProps {
  title?: string;
  fields: PersonalInfoFieldConfig[];
  profileThumbnailUrl: string | null;
  userThumbnail: string | null;
  portalUid: string | undefined;
  profilePictureKey: number;
  onThumbnailPathChange: (path: string | null) => void;
  showProfilePicture?: boolean;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  title = "Personal info",
  fields,
  profileThumbnailUrl,
  userThumbnail,
  portalUid,
  profilePictureKey,
  onThumbnailPathChange,
  showProfilePicture = true,
}) => {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        <Box sx={{ flex: { xs: 1, md: "0 0 60%" } }}>
          <Stack spacing={3}>
            {fields
              .filter((field) => !field.render)
              .map((field) => {
                const label =
                  typeof field.label === "string" ? (
                    <>
                      {field.label}
                      {field.required && (
                        <Box
                          component="span"
                          sx={{ color: `var(--error-700)` }}
                        >
                          {" *"}
                        </Box>
                      )}
                    </>
                  ) : (
                    field.label
                  );

                return (
                  <FormField
                    key={field.key}
                    label={label}
                    value={field.value}
                    onChange={field.onChange}
                    type={field.type}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    startAdornment={field.startAdornment}
                    endAdornment={field.endAdornment}
                    required={field.required}
                    error={!!field.error}
                    helperText={field.error}
                  />
                );
              })}
          </Stack>
          {fields
            .filter((field) => field.render)
            .map((field) => (
              <Box key={field.key} sx={{ mt: 2 }}>
                {field.render!(field)}
              </Box>
            ))}
        </Box>
        {showProfilePicture && (
          <>
            <Divider orientation="vertical" flexItem />
            {portalUid && (
              <ProfilePictureSection
                key={profilePictureKey}
                initialThumbnailUrl={
                  profileThumbnailUrl || userThumbnail || null
                }
                portalUid={portalUid}
                onThumbnailPathChange={onThumbnailPathChange}
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
